/**
 * Malla visual identity for auth screens (kit #13 — de-cloned).
 *
 * After de-cloning, screens stop using the 3-density brand panel
 * (`split`/`hero`/`aside`) from shared kit; custom shell
 * (`auth-kiosk.tsx`) only needs the **brand glyph** and **ambient
 * illustration**. Distinctive motif: a **node mesh** (network constellation),
 * `currentColor`/tokens → theme-safe.
 */

import { For } from "solid-js";

/**
 * Brand glyph for chip: a linked **constellation of nodes** (central
 * hub + satellites + edges). Shares DNA of primary logo.
 * `currentColor` = theme-safe.
 */
export function MallaMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-hidden="true"
    >
      <path
        d="M16 16 16 6 M16 16 6.5 23.5 M16 16 25.5 23.5"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
      />
      <path
        d="M16 6 6.5 23.5 M16 6 25.5 23.5 M6.5 23.5 25.5 23.5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-opacity="0.42"
      />
      <circle cx="16" cy="6" r="2.5" fill="currentColor" />
      <circle cx="6.5" cy="23.5" r="2.5" fill="currentColor" />
      <circle cx="25.5" cy="23.5" r="2.5" fill="currentColor" />
      <circle cx="16" cy="16" r="4.2" fill="currentColor" fill-opacity="0.28" />
      <circle cx="16" cy="16" r="2.9" fill="currentColor" />
    </svg>
  );
}

/**
 * Decorative illustration: a **network mesh** — central node orchestrating
 * a constellation of satellite nodes joined by edges, within two concentric
 * rings (sovereign perimeter). `aria-hidden`, `currentColor` to
 * verse bien en cualquier tema.
 */
export function MeshIllustration(props: { class?: string }) {
  const cx = 160;
  const cy = 160;
  const ring = 96;
  // Six satellite nodes evenly spaced on ring (static → <For> per rule
  // solid/prefer-for, aunque nunca cambia).
  const sats = Array.from({ length: 6 }, (_, i) => {
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    return { x: cx + Math.cos(a) * ring, y: cy + Math.sin(a) * ring };
  });
  return (
    <div class={`pointer-events-none ${props.class ?? ""}`} aria-hidden="true">
      <svg
        viewBox="0 0 320 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="h-full w-auto max-w-none"
      >
        <circle cx={cx} cy={cy} r="150" stroke="currentColor" stroke-width="2" />
        <circle
          cx={cx}
          cy={cy}
          r="122"
          stroke="currentColor"
          stroke-width="2"
          stroke-opacity="0.6"
        />
        {/* Aristas de perímetro (malla)*/}
        <For each={sats}>
          {(s, i) => (
            <line
              x1={s.x}
              y1={s.y}
              x2={sats[(i() + 1) % sats.length]!.x}
              y2={sats[(i() + 1) % sats.length]!.y}
              stroke="currentColor"
              stroke-width="3"
              stroke-opacity="0.4"
            />
          )}
        </For>
        {/* Aristas radiales hub → satélite*/}
        <For each={sats}>
          {(s) => <line x1={cx} y1={cy} x2={s.x} y2={s.y} stroke="currentColor" stroke-width="4" />}
        </For>
        {/* Nodos satélite*/}
        <For each={sats}>{(s) => <circle cx={s.x} cy={s.y} r="11" fill="currentColor" />}</For>
        {/* Nodo central (hub) con halo*/}
        <circle cx={cx} cy={cy} r="26" fill="currentColor" fill-opacity="0.25" />
        <circle cx={cx} cy={cy} r="16" fill="currentColor" />
      </svg>
    </div>
  );
}
